import { useCallback, useRef, useState } from 'react';

import api, { createNamedError, FetchError } from '../../api/api';
import { StudyParticipant } from '../../models/StudyParticipant';

const RETRY_DELAYS_MS = [1000, 3000];
const JSON_READ_TIMEOUT_MS = 10_000;
const SESSION_EXPIRED = {
  statusCode: 401,
  error: 'Unauthorized',
  message: 'Your session has expired. Please sign in again.'
};

function isAbortError(err: any): boolean {
  return err?.name === 'AbortError';
}

function shouldRetry(err: any): boolean {
  if (!err) return false;
  if (isAbortError(err)) return false;
  if (err?.name === 'TimeoutError') return false;
  if (err?.error === 'Unauthorized') return false;
  if (err?.statusCode === 401 || err?.statusCode === 403) return false;
  if (err?.error === 'InvalidResponseBody') return true;
  if (typeof err.statusCode === 'number') {
    return err.statusCode >= 500;
  }
  return true;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createNamedError('AbortError', 'Aborted'));
      return;
    }
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(createNamedError('AbortError', 'Aborted'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

async function readJsonWithTimeout<T>(response: Response): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      response.json(),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(createNamedError('TimeoutError', 'Response body timed out'));
        }, JSON_READ_TIMEOUT_MS);
      })
    ]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

export function useAssignStudyTemplate(
  studyParticipantId: number,
  onAuthFailure?: () => void | Promise<void>
) {
  const [data, setData] = useState<StudyParticipant | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<FetchError | Error | undefined>();

  // Refs to allow callers to cancel an in-flight retry loop. We track:
  // - the latest fetch's abort handle (from api.put)
  // - a controller for the current sleep delay
  // - a flag the loop checks between iterations
  const cancelledRef = useRef(false);
  const activeAbortRef = useRef<(() => void) | null>(null);
  const sleepControllerRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    activeAbortRef.current?.();
    sleepControllerRef.current?.abort();
  }, []);

  const assignStudyTemplate = useCallback(
    async (studyTemplateId: number): Promise<StudyParticipant> => {
      if (!studyParticipantId || studyParticipantId <= 0) {
        const invalidIdError = {
          statusCode: 400,
          error: 'BadRequest',
          message: 'Study participant is not available yet.'
        };
        setError(invalidIdError);
        throw invalidIdError;
      }

      cancelledRef.current = false;
      activeAbortRef.current = null;
      sleepControllerRef.current = null;

      const date = new Date();
      const utcOffset = date.getTimezoneOffset() / 60;
      const path = `study-participants/${studyParticipantId}/study-template`;
      const body = { studyTemplateId, utcOffset };

      setIsLoading(true);
      setError(undefined);

      let lastError: any;
      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
        if (cancelledRef.current) {
          setIsLoading(false);
          throw createNamedError('AbortError', 'Aborted');
        }
        if (attempt > 0) {
          const sleepController = new AbortController();
          sleepControllerRef.current = sleepController;
          try {
            await sleep(RETRY_DELAYS_MS[attempt - 1], sleepController.signal);
          } catch (err) {
            setIsLoading(false);
            throw err;
          } finally {
            sleepControllerRef.current = null;
          }
        }
        try {
          const { response, abort } = api.put(path, body);
          activeAbortRef.current = abort;
          const result = await response;
          activeAbortRef.current = null;

          if (result.ok) {
            let json: StudyParticipant;
            try {
              json = await readJsonWithTimeout<StudyParticipant>(result);
            } catch (err) {
              if ((err as Error)?.name === 'TimeoutError') {
                lastError = err;
              } else if (
                !(result.headers.get('content-type') || '').includes(
                  'application/json'
                )
              ) {
                lastError = { ...SESSION_EXPIRED, statusCode: result.status };
              } else {
                lastError = {
                  statusCode: result.status,
                  error: 'InvalidResponseBody',
                  message: result.statusText || 'Invalid response body'
                };
              }
              if (!shouldRetry(lastError)) break;
              continue;
            }
            if (cancelledRef.current) {
              setIsLoading(false);
              throw createNamedError('AbortError', 'Aborted');
            }
            setData(json);
            setIsLoading(false);
            return json;
          }

          const contentType = result.headers.get('content-type') || '';
          try {
            lastError = await readJsonWithTimeout(result);
          } catch (err) {
            if ((err as Error)?.name === 'TimeoutError') throw err;
            if (
              result.status === 401 ||
              result.status === 403 ||
              !contentType.includes('application/json')
            ) {
              lastError = {
                ...SESSION_EXPIRED,
                statusCode: result.status || 401
              };
            } else {
              lastError = {
                statusCode: result.status,
                message: result.statusText
              };
            }
          }
          if (!shouldRetry(lastError)) break;
        } catch (err) {
          activeAbortRef.current = null;
          if (isAbortError(err)) {
            setIsLoading(false);
            throw err;
          }
          lastError = err;
          if (!shouldRetry(err)) break;
        }
      }

      if (lastError?.error === 'Unauthorized') {
        await onAuthFailure?.();
      }

      setError(lastError);
      setIsLoading(false);
      throw lastError;
    },
    [studyParticipantId, onAuthFailure]
  );

  return {
    updatedStudyParticipant: data,
    assigningStudyTemplate: isLoading,
    assigningStudyTemplateError: error,
    assignStudyTemplate,
    cancel
  };
}
